import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Carrier {
  id: string;
  name: string;
  description: string | null;
  order: number | null;
}

export const useCarriers = () => {
  const queryClient = useQueryClient();

  const { data: carriers, isLoading } = useQuery({
    queryKey: ["carriers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carriers")
        .select("*")
        .order("order", { ascending: true });

      if (error) throw error;
      return data as Carrier[];
    },
  });

  const updateOrder = useMutation({
    mutationFn: async (carriers: Carrier[]) => {
      const updates = carriers.map((carrier, index) => ({
        id: carrier.id,
        name: carrier.name,
        description: carrier.description,
        order: index + 1,
      }));

      const { error } = await supabase
        .from("carriers")
        .upsert(updates, { onConflict: "id" });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
      toast.success("Carrier order updated successfully");
    },
    onError: (error) => {
      console.error("Error updating carrier order:", error);
      toast.error("Failed to update carrier order");
    },
  });

  const createCarrier = useMutation({
    mutationFn: async (formData: Omit<Carrier, "id" | "order">) => {
      const { error } = await supabase
        .from("carriers")
        .insert([formData]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
      toast.success("Carrier created successfully");
    },
    onError: (error) => {
      console.error("Error creating carrier:", error);
      toast.error("Failed to create carrier");
    },
  });

  const updateCarrier = useMutation({
    mutationFn: async ({ id, ...formData }: Carrier) => {
      const { error } = await supabase
        .from("carriers")
        .update(formData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
      toast.success("Carrier updated successfully");
    },
    onError: (error) => {
      console.error("Error updating carrier:", error);
      toast.error("Failed to update carrier");
    },
  });

  const deleteCarrier = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("carriers")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
      toast.success("Carrier deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting carrier:", error);
      toast.error("Failed to delete carrier");
    },
  });

  return {
    carriers,
    isLoading,
    updateOrder,
    createCarrier,
    updateCarrier,
    deleteCarrier,
  };
};